import { useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { useQuery } from "react-query";
import { useState, useMemo, useCallback } from "react";
import { Flex, Text, Button, Badge, Table, IconButton, Section, TextField } from "@radix-ui/themes";
import Tooltip from "@/components/shared/tooltip/Tooltip";
import { Kanban } from "@/v2/assets/icons/Kanban";
import { useNavigate } from "react-router-dom";
import { BaseTracker } from "@/v2/entities/tracker/Tracker.model";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import {
  Cross1Icon,
  DotsVerticalIcon,
  Pencil1Icon,
  ArchiveIcon,
  ChevronDownIcon,
  PlusIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { useQueryClient } from "react-query";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { toast } from "sonner";
import { InfoIcon } from "lucide-react";
import { RuleBuilder } from "@/v2/features/sampling/RuleBuilder";
import { QaRules } from "@/v2/entities/sampling/Sampling.api";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";

function TrackerRow({ tracker }: { tracker: BaseTracker }) {
  const navigate = useNavigate();
  const { archiveTracker } = useTrackerApi();
  const queryClient = useQueryClient();

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/trackers/${tracker.id}/edit`, {
        state: { from: "list" },
      });
    },
    [navigate, tracker.id]
  );

  const handleArchive = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        const isArchived = !tracker.isArchived;
        await archiveTracker(tracker.id, isArchived);
        queryClient.invalidateQueries(["trackers"]);
        toast(
          isArchived
            ? "Tracker archived successfully"
            : "Tracker unarchived successfully"
        );
      } catch (error) {
        console.error("Failed to archive tracker:", error);
        toast.error("Failed to update tracker status");
      }
    },
    [archiveTracker, tracker.id, tracker.isArchived, queryClient]
  );

  const handleRowClick = useCallback(() => {
    navigate(`/trackers/${tracker.id}`);
  }, [navigate, tracker.id]);

  return (
    <Table.Row
      style={{ cursor: "pointer", height: "64px" }}
      onClick={handleRowClick}
    >
      <Table.Cell style={{ verticalAlign: "middle" }}>
        <Flex align="center" gap="2">
          <Flex
            align="center"
            justify="center"
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "var(--teal-3)",
              borderRadius: "4px",
            }}
          >
            <Kanban size={16} fill="teal" />
          </Flex>
          <Flex align="center" gap="2">
            <Text size={"2"} color="gray" weight="medium" highContrast>
              {tracker.name}
            </Text>
            {tracker.sources && tracker.sources.length > 0 && (
              <Flex align="center" gap="1">
                {tracker.sources.includes("tickets") && (
                  <SimulatorChannelIcon
                    channel="autonomous_ticket_event"
                    size="1"
                  />
                )}
                {tracker.sources.includes("conversations") && (
                  <SimulatorChannelIcon
                    channel="autonomous_quackchat"
                    size="1"
                  />
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Table.Cell>
      <Table.Cell style={{ verticalAlign: "middle" }}>
        <span
          className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${
            tracker.isArchived
              ? "bg-gray-100 text-gray-500"
              : "bg-green-100 text-green-700"
          }`}
        >
          {tracker.isArchived ? "Archived" : "Active"}
        </span>
      </Table.Cell>
      <Table.Cell style={{ verticalAlign: "middle" }}>
        {/* Capture Rules as badges with overflow */}

        <RuleBuilder
          key={tracker.id}
          size="1"
          value={tracker.matchRules as QaRules}
          viewOnly={true}
          onChange={(newQaRules) => {}}
        />
      </Table.Cell>
      <Table.Cell style={{ verticalAlign: "middle" }}>
        {(() => {
          return (
            <div
              style={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
                alignItems: "center",
                maxWidth: 220,
              }}
            >
              <Badge color="gray">{tracker.itemsCountByStage.to_do}</Badge>
            </div>
          );
        })()}
      </Table.Cell>
      <Table.Cell style={{ verticalAlign: "middle" }}>
        <DropdownMenu
          items={[
            {
              label: "Edit",
              icon: <Pencil1Icon />,
              onClick: handleEdit,
            },
            {
              label: tracker.isArchived ? "Unarchive" : "Archive",
              icon: <ArchiveIcon />,
              onClick: handleArchive,
            },
          ]}
        >
          <DropdownMenu.Trigger>
            <IconButton
              variant="ghost"
              color="gray"
              size="2"
              onClick={(e) => e.stopPropagation()}
            >
              <DotsVerticalIcon width={16} height={16} />
            </IconButton>
          </DropdownMenu.Trigger>
        </DropdownMenu>
      </Table.Cell>
    </Table.Row>
  );
}

function TrackersList() {
  const trackerApi = useTrackerApi();
  const [searchPhrase, setSearchPhrase] = useState("");
  const [archiveFilter, setArchiveFilter] = useState<
    "active" | "archived" | "all"
  >("active");

  const { data: trackers, isLoading } = useQuery({
    queryKey: ["trackers"],
    queryFn: trackerApi.getTrackers,
  });

  const filteredTrackers = useMemo(() => {
    if (!trackers) return [];
    let filtered = trackers;
    if (archiveFilter === "active") {
      filtered = filtered.filter((tracker) => !tracker.isArchived);
    } else if (archiveFilter === "archived") {
      filtered = filtered.filter((tracker) => tracker.isArchived);
    }
    if (searchPhrase) {
      filtered = filtered.filter((tracker) =>
        tracker.name.toLowerCase().includes(searchPhrase.toLowerCase())
      );
    }
    return filtered;
  }, [trackers, searchPhrase, archiveFilter]);

  const navigate = useNavigate();

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Trackers"
          description="Monitor and manage interactions that match specific criteria across different stages"
          icon={<Kanban size={24} fill={PageColors[ENavigate.TRACKERS] || "gray"} />}
          actions={
            <Flex gap="2" align="center">
              <TextField.Root
                placeholder="Search Trackers"
                size="2"
                value={searchPhrase}
                onChange={(e) => setSearchPhrase(e.target.value)}
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
              {searchPhrase && (
                <Button
                  variant="ghost"
                  size="1"
                  color="gray"
                  highContrast
                  onClick={() => setSearchPhrase("")}
                  aria-label="Clear search"
                >
                  <Cross1Icon />
                </Button>
              )}
              <DropdownMenu
                items={[
                  {
                    label: "Active",
                    onClick: () => setArchiveFilter("active"),
                  },
                  {
                    label: "Archived",
                    onClick: () => setArchiveFilter("archived"),
                  },
                  {
                    label: "All",
                    onClick: () => setArchiveFilter("all"),
                  },
                ]}
              >
                <DropdownMenu.Trigger>
                  <Button
                    variant="outline"
                    color="gray"
                    size="2"
                    highContrast
                    style={{
                      width: 110,
                      justifyContent: "space-between",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {archiveFilter === "active"
                      ? "Active"
                      : archiveFilter === "archived"
                      ? "Archived"
                      : "All"}
                    <ChevronDownIcon style={{ marginLeft: 8 }} />
                  </Button>
                </DropdownMenu.Trigger>
              </DropdownMenu>
              <Button
                highContrast
                color="gray"
                onClick={() => navigate("/trackers/new")}
              >
                <PlusIcon />
                <Text>Create Tracker</Text>
              </Button>
            </Flex>
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <Flex direction="row" gap="2" align="center">
              <Flex direction="row" gap="2" align="center">
                <Text size="3" weight="medium">
                  Trackers
                </Text>
                <Badge color="gray">{filteredTrackers.length}</Badge>
              </Flex>
            </Flex>

          <Table.Root>
            <Table.Header>
              <Table.Row
                className="text-gray-500 font-normal"
                style={{ height: "48px" }}
              >
                <Table.ColumnHeaderCell
                  className="font-normal w-1/3"
                  style={{ verticalAlign: "middle" }}
                >
                  Name
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell
                  className="font-normal w-1/6"
                  style={{ verticalAlign: "middle" }}
                >
                  Status
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell
                  className="font-normal w-1/3"
                  style={{ verticalAlign: "middle" }}
                >
                  <Tooltip>
                    <Tooltip.Trigger>
                      <Text
                        className={`flex items-center gap-1 cursor-pointer`}
                      >
                        Capture Rules
                        <InfoIcon className=" h-4 w-4" />
                      </Text>
                    </Tooltip.Trigger>

                    <Tooltip.Content>
                      <Text size="1" color="gray">
                        <Flex gap="2" direction="column">
                          The types of interactions this tracker looks out for
                        </Flex>
                      </Text>
                    </Tooltip.Content>
                  </Tooltip>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell
                  className="font-normal w-1/3"
                  style={{ verticalAlign: "middle" }}
                >
                  <Tooltip>
                    <Tooltip.Trigger>
                      <Text
                        className={`flex items-center gap-1 cursor-pointer`}
                      >
                        Captured Items
                        <InfoIcon className=" h-4 w-4" />
                      </Text>
                    </Tooltip.Trigger>

                    <Tooltip.Content>
                      <Text size="1" color="gray">
                        <Flex gap="2" direction="column">
                          Recent interactions (last 14 days) waiting for your
                          review
                        </Flex>
                      </Text>
                    </Tooltip.Content>
                  </Tooltip>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell
                  className="font-normal w-1/12"
                  style={{ verticalAlign: "middle" }}
                ></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <Table.Row key={index} style={{ height: "64px" }}>
                    <Table.Cell style={{ verticalAlign: "middle" }}>
                      <div
                        className="skeleton-item"
                        style={{
                          height: "20px",
                          width: "60%",
                          background: "var(--gray-3)",
                          borderRadius: "4px",
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell style={{ verticalAlign: "middle" }}>
                      <div
                        className="skeleton-item"
                        style={{
                          height: "20px",
                          width: "40%",
                          background: "var(--gray-3)",
                          borderRadius: "4px",
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell style={{ verticalAlign: "middle" }}>
                      <div
                        className="skeleton-item"
                        style={{
                          height: "20px",
                          width: "80%",
                          background: "var(--gray-3)",
                          borderRadius: "4px",
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell style={{ verticalAlign: "middle" }}>
                      <div
                        className="skeleton-item"
                        style={{
                          height: "20px",
                          width: "80%",
                          background: "var(--gray-3)",
                          borderRadius: "4px",
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell style={{ verticalAlign: "middle" }}>
                      <div
                        className="skeleton-item"
                        style={{
                          height: "20px",
                          width: "20px",
                          background: "var(--gray-3)",
                          borderRadius: "4px",
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : filteredTrackers.length === 0 ? (
                <Table.Row style={{ height: "64px" }}>
                  <Table.Cell colSpan={5} style={{ verticalAlign: "middle" }}>
                    <Flex
                      align="center"
                      justify="center"
                      py="6"
                      direction="column"
                      gap="2"
                    >
                      <Text size="2" color="gray">
                        No trackers found
                      </Text>
                      <Text size="2" color="gray">
                        {searchPhrase
                          ? "Try adjusting your search terms"
                          : "Trackers will appear here when created"}
                      </Text>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : (
                filteredTrackers.map((tracker) => (
                  <TrackerRow key={tracker.id} tracker={tracker} />
                ))
              )}
            </Table.Body>
          </Table.Root>
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}

export default TrackersList;
